import { LinkBox } from '@hover/blueprint';
import React from 'react';
import { generatePath } from 'react-router';
import { Link } from 'react-router-dom';

import { useHomeownerAppContext } from 'src/modules/homeownerApp/hooks';
import type { AiOrSampleOriginalImage } from 'src/modules/homeownerApp/types';
import {
  isSampleGenerated,
  isSampleOriginal,
} from 'src/modules/homeownerApp/types';
import { Route as Routes } from 'src/shared/route';
import { imageClicked } from 'src/shared/telemetry';

import { ImageCard } from '../../ImageCard';
import type { MasonryProps } from '../../Masonry';
import { Masonry, MasonryItem } from '../../Masonry';
import { UploadPhotoInput } from '../UploadPhotoInput/UploadPhotoInput';

const PRELOAD_IMAGE_THRESHOLD = 6;

interface MasonryGridProps extends Partial<MasonryProps> {
  images: AiOrSampleOriginalImage[] | null;
  isLoading: boolean;
  isSampleSpaces?: boolean;
}

const MasonryGrid = ({
  images,
  isLoading,
  isSampleSpaces,
  ...props
}: MasonryGridProps) => {
  const { trackingData } = useHomeownerAppContext();

  return (
    <Masonry
      columns={{ base: 2, tablet: 3 }}
      fallbackRowHeight={{ base: 250, width250: 300, tablet: 400 }}
      gap={{ base: 300, width250: 400, tablet: 500 }}
      isLoading={isLoading}
      preloadImages={images?.slice(0, PRELOAD_IMAGE_THRESHOLD).flatMap(i => {
        const generatedImagePath = isSampleOriginal(i)
          ? i.sampleGeneratedImages?.[0]?.thumbnailUrl
          : i.aiGeneratedImages?.[0]?.thumbnailUrl;

        const originalThumbnailUrl = i.thumbnailUrl || '';

        return generatedImagePath
          ? [generatedImagePath, originalThumbnailUrl]
          : [originalThumbnailUrl];
      })}
      px={{ base: '500', tablet: '800' }}
      py={{ base: '500', tablet: '600' }}
      {...props}
    >
      {images?.map(image => {
        const { createdAt, thumbnailUrl, spaceType, orientation } = image;
        const isSampleImage = isSampleOriginal(image);

        const generatedImage = isSampleImage
          ? image.sampleGeneratedImages?.[0]
          : image.aiGeneratedImages?.[0];

        const id = isSampleImage ? image.sampleId : image.id;

        return (
          <MasonryItem key={id}>
            <LinkBox>
              <Link
                onClick={() => {
                  imageClicked({
                    ...trackingData,
                    ai_original_image_id: isSampleImage
                      ? undefined
                      : Number(id),
                    ai_generated_image_id: isSampleGenerated(generatedImage)
                      ? undefined
                      : generatedImage?.id,
                    output_id: isSampleImage ? String(id) : undefined,
                    track_id: 'open-upload',
                    space_type: spaceType || undefined,
                  });
                }}
                style={{ textDecoration: 'none' }}
                to={generatePath(Routes.HOMEOWNER_APP_ORIGINAL.path, {
                  aiOriginalImageId: id,
                })}
              >
                <ImageCard
                  createdAt={createdAt || ''}
                  orientation={orientation}
                  primaryImageUrl={
                    generatedImage?.thumbnailUrl ||
                    generatedImage?.generatedImagePath
                  }
                  spaceType={spaceType}
                  subText={image?.associatedProject?.name}
                  thumbnailImageUrl={thumbnailUrl || ''}
                  width="100%"
                />
              </Link>
            </LinkBox>
          </MasonryItem>
        );
      })}
      {images && images.length < 3 && !isSampleSpaces && (
        <MasonryItem>
          <UploadPhotoInput />
        </MasonryItem>
      )}
    </Masonry>
  );
};

export { MasonryGrid };
